import { useCallback, useEffect, useRef, useState } from 'react';

import { MAX_DURATION_SEC } from '../../../components/VideoRecorder/components/RecorderTimeLimit';
import { useStopRecordingWhenHidden } from '../useStopRecordingWhenHidden';

const MAX_DURATION_MS = MAX_DURATION_SEC * 1000;
const DURATION_TICK_MS = 250;

type Args = {
	isRecording: boolean;
	/** The limit was reached. Stop the take. */
	onMaxDuration: () => void;
	/** The page went away mid-take. Stop the take while there is still a chance to. */
	onBackgrounded: () => void;
};

type Result = {
	/** Seconds elapsed in the current take, or undefined when nothing is rolling. */
	duration: number | undefined;
	/**
	 * Seconds elapsed, read at call time rather than from state - the timer callbacks would see a stale
	 * closure. Undefined outside a take.
	 */
	getElapsedSeconds: () => number | undefined;
};

/**
 * The recording clock: elapsed time, the duration cutoff, and stopping when the page is backgrounded.
 *
 * Shared rather than reimplemented per screen. Both timers read the wall clock rather than the camera
 * preview's `timeupdate` event, which stops firing when the tab or app is backgrounded while
 * `MediaRecorder` keeps rolling - that let recordings run well past the limit (CHANGE-97165). Any second
 * copy of this is a second place for that bug to come back.
 *
 * Elapsed seconds only, deliberately: how the time is *shown* is an arm-level decision. The recorder
 * screen counts up to the limit; `v2_consolidated` counts down from it.
 */
export function useRecordingDuration({ isRecording, onMaxDuration, onBackgrounded }: Args): Result {
	const [elapsed, setElapsed] = useState(0);
	const startedAtRef = useRef<number | undefined>(undefined);

	const getElapsedSeconds = useCallback(() => {
		const startedAt = startedAtRef.current;
		return startedAt === undefined ? undefined : Math.floor((Date.now() - startedAt) / 1000);
	}, []);

	// The recording effect below depends on `isRecording` alone. Callers rebuild these handlers on every
	// render, so listing them as dependencies would re-arm the cutoff each render and it would never fire.
	const onMaxDurationRef = useRef(onMaxDuration);
	useEffect(() => {
		onMaxDurationRef.current = onMaxDuration;
	}, [onMaxDuration]);

	useEffect(() => {
		if (!isRecording) {
			return () => {};
		}

		const startedAt = Date.now();
		startedAtRef.current = startedAt;

		const ticker = setInterval(() => {
			setElapsed(Math.floor((Date.now() - startedAt) / 1000));
		}, DURATION_TICK_MS);

		const cutoff = setTimeout(() => {
			onMaxDurationRef.current();
		}, MAX_DURATION_MS);

		return () => {
			clearInterval(ticker);
			clearTimeout(cutoff);
			// Cleared so this take's start can never be mistaken for the next take's. Safe to do here: a
			// caller stopping the take reads the elapsed time before `isRecording` flips, since
			// `MediaRecorder.stop` resolves through `onstop` a turn later.
			startedAtRef.current = undefined;
			// Zeroed on the way out rather than on the way in. `isRecording` turns true in a render before
			// this effect can run, so resetting at the start of a take leaves one render showing the previous
			// take's count. Here `duration` is already `undefined` - nothing is displaying it - so the reset
			// is invisible, and the next take begins at zero.
			setElapsed(0);
		};
	}, [isRecording]);

	// Deliberately after the effect above: that one records this take's start time, and the hook below
	// fires `onBackgrounded` synchronously when the page is already hidden. Registered the other way
	// round, the stop would read a start time belonging to the previous take. See CHANGE-97165.
	useStopRecordingWhenHidden(isRecording, onBackgrounded);

	return {
		duration: isRecording ? elapsed : undefined,
		getElapsedSeconds,
	};
}
